import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { permissionScope, hasPermission } from '@/composables/usePermission'

export default function useConfirmationPlanningList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const refConfirmationPlanningTable = ref(null)
  const confirmationPlannings = ref([])
  const { updateItem } = useLambda()
  const { confirmationPlanningQueryWithOffset } = useMongoDBFunctions()
  const collection = 'confirmation_planning'

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      // {
      //   key: 'roleName.name',
      //   label: i18n.t('message.tableHeader.role'),
      //   sortable: true,
      // },
      {
        key: 'processData.code',
        label: i18n.t('message.tableHeader.code'),
        sortable: true,
      },
      {
        key: 'processData.name',
        label: i18n.t('message.tableHeader.process'),
        sortable: true,
      },
      {
        key: 'evaluatedName.name',
        label: i18n.t('label.evaluated'),
        sortable: true,
      },
      {
        key: 'confirmationsPerYear',
        label: i18n.t('message.tableHeader.confirmationsPerYear'),
        sortable: true,
      },
    ]
  })
  const perPage = ref(10)
  const totalConfirmationPlannings = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('processData.code')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  // Leer processFilter desde el store global
  const processFilter = computed(() => store.state.filtersStore.processFilter)
  const companyFilter = computed(() => store.state.filtersStore.companyFilter)
  const idFilter = computed(() => store.state.filtersStore.idFilter)
  const workerFilter = ref([]);
  const processTypeFilter = ref([])
  // const roleFilter = ref([]);

  const refetchData = () => {
    if (refConfirmationPlanningTable.value) {
      refConfirmationPlanningTable.value.refresh()
    }
  }

  const fetchConfirmationPlannings = async () => {
    isLoading.value = true
    const { role, client = null, worker_id } = store.state?.userStore?.userData
    
    const input = {
      limit: perPage.value,
      total: currentPage.value === 1,
      offset: (currentPage.value * perPage.value) - perPage.value,
      search: searchQuery.value,
      sortBy: sortBy.value,
      sortOrder: isSortDirDesc.value ? -1 : 1,
      processes: processFilter.value,
      evaluated: workerFilter.value,
      processTypeFilter: processTypeFilter.value,
      // roles: roleFilter.value,
      client_id: (role !== 'admin') ? client.$oid : null,
      restrictToOwn: permissionScope('confirmation-planning', 'view') === 'own',
      worker_id: worker_id ? worker_id.$oid : null,
      companyFilter: companyFilter.value,
      idFilter: idFilter.value,
    }

    try {
        const items = await confirmationPlanningQueryWithOffset(input)

        const { confirmationPlannings: c, totalRecords: total } = items || {}
        confirmationPlannings.value = c
        if (currentPage.value === 1) totalConfirmationPlannings.value = total
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_planning') })
      } finally {
        isLoading.value = false
      }
  }

  const deleteConfirmationPlanning = async (id) => {
    if (!hasPermission('confirmation-planning', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const { role, client = null } = store.state?.userStore?.userData
      const clientId = role !== 'admin' ? client?.$oid : null
      const query = { _id: oid(id) }
      if (clientId) query.client_id = oid(clientId)
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      showSuccessMessage(i18n.t('message.confirmation_planning_remove'))
      fetchConfirmationPlannings()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_planning_remove_error'))
    }
  }

  watch([currentPage, perPage], () => {
    fetchConfirmationPlannings()
  })

  watch([searchQuery, sortBy, isSortDirDesc, companyFilter, idFilter], () => {
    currentPage.value = 1
    fetchConfirmationPlannings()
  })

  return {
    fetchConfirmationPlannings,
    tableColumns,
    perPage,
    currentPage,
    totalConfirmationPlannings,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refConfirmationPlanningTable,
    confirmationPlannings,
    refetchData,
    deleteConfirmationPlanning,
    processFilter,
    workerFilter,
    processTypeFilter,
    // roleFilter,
    isLoading,
  }
}
